import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

type Candidate = { personId: string; name: string; ministries: string[]; canOperate: boolean };
type AssistMinistry = { key: string; name: string; messages: string[]; available: boolean };
type AssistRequest = { ministry: string; ministryName: string; message: string; by: string; at: string };

const plain = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Consola → «Control desde el celular» → «Enviar a líderes»: elige líderes y colíderes de los
 * ministerios y les llega una notificación con el enlace al control. Solo a quien ya tiene permiso
 * de operar presentaciones (no se regalan permisos).
 */
export function RemoteInvite({ churchId, presentationId }: { churchId: string; presentationId: string }) {
  const [open, setOpen] = useState(false);
  const base = `/presentation/churches/${churchId}/presentations/${presentationId}/remote`;
  const list = useAsync((signal) => (open ? api.get<{ items: Candidate[] }>(`${base}/candidates`, signal).then((r) => r.items) : Promise.resolve([] as Candidate[])), [open, base]);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (!open) {
    return (
      <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
        Enviar a líderes
      </Button>
    );
  }
  const items = list.status === 'ready' ? list.data : [];
  const shown = q.trim() ? items.filter((c) => plain(`${c.name} ${c.ministries.join(' ')}`).includes(plain(q.trim()))) : items;
  const without = items.filter((c) => !c.canOperate).length;

  async function send() {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const r = await api.post<{ sent: number; skipped: string[] }>(`${base}/invite`, { personIds: [...picked] });
      setDone(r.sent === 1 ? 'Se mandó el enlace a 1 persona.' : `Se mandó el enlace a ${r.sent} personas.`);
      setPicked(new Set());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="remote-invite">
      <label className="visually-hidden" htmlFor="remote-invite-q">
        Buscar líder
      </label>
      <input id="remote-invite-q" type="search" placeholder="Buscar líder o ministerio" value={q} onChange={(e) => setQ(e.target.value)} />
      {list.status === 'loading' ? <p className="small muted">Cargando líderes…</p> : null}
      {list.status === 'error' ? <Alert>{errorMessage(list.error)}</Alert> : null}
      <ul className="remote-invite__list" aria-label="Líderes y colíderes">
        {shown.map((c) => (
          <li key={c.personId}>
            <label className={c.canOperate ? undefined : 'remote-invite__off'}>
              <input
                type="checkbox"
                disabled={!c.canOperate}
                checked={picked.has(c.personId)}
                onChange={(e) => {
                  const next = new Set(picked);
                  if (e.target.checked) next.add(c.personId);
                  else next.delete(c.personId);
                  setPicked(next);
                }}
              />
              <span>
                <strong>{c.name}</strong> <span className="small muted">{c.ministries.join(', ')}</span>
                {!c.canOperate ? <span className="small"> · sin permiso para el control</span> : null}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {without > 0 ? (
        <p className="small muted">
          Quien dice «sin permiso para el control» no puede usarlo todavía: la administración le da el permiso de operar presentaciones (en Roles y permisos). Desde aquí no se dan permisos.
        </p>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      {done ? (
        <p className="small ewc__done" role="status">
          {done}
        </p>
      ) : null}
      <div className="actions actions--wrap">
        <Button type="button" busy={busy} disabled={picked.size === 0} onClick={() => void send()}>
          {picked.size > 0 ? `Enviar enlace (${picked.size})` : 'Enviar enlace'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
          Cerrar
        </Button>
      </div>
    </div>
  );
}

/** Consola: lo que le pidieron a Multimedia desde el control por celular (se quita con «Listo»). */
export function AssistBanner({ churchId, presentationId }: { churchId: string; presentationId: string }) {
  const url = `/presentation/churches/${churchId}/presentations/${presentationId}/assist`;
  const [items, setItems] = useState<AssistRequest[]>([]);
  const [seen, setSeen] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    let alive = true;
    const load = () =>
      void api
        .get<{ items: AssistRequest[] }>(url)
        .then((r) => alive && setItems(r.items))
        .catch(() => undefined);
    load();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [url]);
  const pending = items.filter((r) => !seen.has(r.at));
  if (pending.length === 0) return null;
  const r = pending[pending.length - 1]!;
  const hour = new Date(r.at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="assist-banner" role="alert">
      <span className="assist-banner__dot" aria-hidden="true" />
      <strong>{r.message}</strong>
      <span className="small">
        {r.by} · {hour}
        {pending.length > 1 ? ` · y ${pending.length - 1} más` : ''}
      </span>
      <button type="button" className="btn btn--secondary" onClick={() => setSeen(new Set([...seen, ...pending.map((x) => x.at)]))}>
        Listo
      </button>
    </div>
  );
}

/** Control por celular → «Solicitar asistencia»: elige ministerio y mensaje (o escribe uno). */
export function AssistPanel({ churchId, presentationId }: { churchId: string; presentationId: string }) {
  const [open, setOpen] = useState(false);
  const data = useAsync((signal) => (open ? api.get<{ items: AssistMinistry[] }>(`/presentation/churches/${churchId}/assist-messages`, signal).then((r) => r.items.filter((m) => m.available)) : Promise.resolve([] as AssistMinistry[])), [open, churchId]);
  const [ministry, setMinistry] = useState<AssistMinistry | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function send(message: string) {
    if (!ministry || !message.trim()) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const r = await api.post<{ notified: number }>(`/presentation/churches/${churchId}/presentations/${presentationId}/assist`, { ministry: ministry.key, message: message.trim() });
      setDone(`Se avisó a ${ministry.name}${r.notified ? ` (${r.notified} ${r.notified === 1 ? 'persona' : 'personas'})` : ''}: «${message.trim()}».`);
      setText('');
      navigator.vibrate?.(30);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn--secondary remote__assist" onClick={() => setOpen(true)}>
        Solicitar asistencia
      </button>
    );
  }
  return (
    <section className="card remote-assist" aria-label="Solicitar asistencia">
      <div className="remote-assist__head">
        <h2>{ministry ? ministry.name : 'Solicitar asistencia'}</h2>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            if (ministry) setMinistry(null);
            else setOpen(false);
            setDone(null);
            setError(null);
          }}
        >
          {ministry ? '← Ministerios' : 'Cerrar'}
        </button>
      </div>
      {error ? <Alert>{error}</Alert> : null}
      {done ? (
        <p className="small ewc__done" role="status">
          {done}
        </p>
      ) : null}
      {!ministry ? (
        <div className="remote-assist__grid">
          {data.status === 'loading' ? <p className="small muted">Cargando…</p> : null}
          {(data.status === 'ready' ? data.data : []).map((m) => (
            <button key={m.key} type="button" className="btn btn--secondary remote-assist__btn" onClick={() => setMinistry(m)}>
              {m.name}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="remote-assist__grid">
            {ministry.messages.map((m) => (
              <button key={m} type="button" className="btn btn--secondary remote-assist__btn" disabled={busy} onClick={() => void send(m)}>
                {m}
              </button>
            ))}
          </div>
          <form
            className="remote-assist__free"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              void send(text);
            }}
          >
            <label className="visually-hidden" htmlFor="assist-text">
              Otro mensaje
            </label>
            <input id="assist-text" maxLength={200} placeholder="Otro mensaje…" value={text} onChange={(e) => setText(e.target.value)} />
            <Button type="submit" busy={busy} disabled={!text.trim()}>
              Enviar
            </Button>
          </form>
        </>
      )}
    </section>
  );
}
